import { z } from 'zod'

import { calendarDateSchema } from '@/schemas/portfolio'
import {
  BROKER_PERIODS,
  brokerListQuerySchema,
  brokerPeriodSchema,
  brokerReviewsQuerySchema,
  brokerStocksQuerySchema,
  busyNamesQuerySchema,
  largeHoldingsQuerySchema,
  largeTradesQuerySchema,
  pvRangeQuerySchema,
  type BrokerDateRange,
  type BrokerListQuery,
  type BrokerPeriod,
  type BrokerReviewsQuery,
  type BrokerStocksQuery,
  type BusyNamesQuery,
  type LargeHoldingsQuery,
  type LargeTradeLeadersQuery,
  type LargeTradeSize,
  type LargeTradesQuery,
  type PVRangeQuery,
} from '@/schemas/broker'

/**
 * Route search-param schemas for the Broker Chirfaar family.
 *
 * Kept out of `src/schemas/`, which is where response/entity schemas for the
 * family belong (`src/schemas/broker.ts`) — that file is the single source of
 * truth for domain vocabulary (periods, large-trade size bands, sort keys,
 * the paged-query shapes). This module composes route search schemas FROM
 * that vocabulary; it does not redefine any of it. Where a route's search
 * shape is exactly one of `schemas/broker.ts`'s query schemas, the export
 * below IS that schema (same reference), not a lookalike, so `Route.useSearch()`
 * can be handed straight to the matching query hook with no adapter.
 *
 * Kept out of the `*Screen.tsx` files too, even though each screen is the
 * only place its schema is used: every one of those calls `createFileRoute`,
 * which only type-checks once `src/routeTree.gen.ts` has been generated, and
 * that file is gitignored and not present on a clean checkout (see
 * docs/TESTING.md, "no test may import a route file"). A schema that a
 * colocated test needs to import therefore cannot live in the route file
 * without dragging that requirement into the test. This module has no
 * `createFileRoute` call, so it is safe to import from both a screen and a
 * test.
 */

// --- period ------------------------------------------------------------------------

/**
 * `today | 1w | 1m | 3m`, everywhere the API takes a period. It is a count of
 * *trading days* off the real `nepse_trade_date` calendar, never calendar
 * days — the window is computed backend-side; the UI only ever names it.
 *
 * Re-exported from `src/schemas/broker.ts` (`BROKER_PERIODS` /
 * `brokerPeriodSchema`) under the route-facing names this module's callers
 * already use. Do not redeclare the enum here — one `z.enum` call per
 * concept in the codebase.
 */
export const PERIODS = BROKER_PERIODS
export const periodSchema = brokerPeriodSchema
export type Period = BrokerPeriod
export const DEFAULT_PERIOD: Period = 'today'

// --- overview --------------------------------------------------------------------------

/**
 * Overview (Figma primary frame `997:31128`) has no tabs of its own — the
 * buy/sell toggle inside its two ranked lists (`3153:130` "Sold selected") is
 * component-local state, not a URL concern, matching how Market's own
 * Overview has no `validateSearch` for its component-local toggles either.
 * The only URL-level state this route carries is the family `period` toggle.
 *
 * `useBrokerOverview(period)` takes `period` positionally, so
 * `Route.useSearch().period` passes straight through with no conversion.
 */
export const overviewSearchSchema = z.object({
  period: periodSchema.default(DEFAULT_PERIOD),
})
export type OverviewSearch = z.infer<typeof overviewSearchSchema>

// --- by broker -----------------------------------------------------------------------

/**
 * The three tabs inside a broker's own page (Figma states `3184:2` Activity,
 * `3184:898` Specialty; the primary frame `2323:16722` is the default,
 * unnamed "Overview" state). Reviews is a separate route
 * (`/brokers/$code/reviews`), not a fourth tab here.
 *
 * Route-only vocabulary: the three tabs are CLIENT-SIDE toggles over one
 * `BrokerDetail` response (see `WAVE1-HANDOFFS.md`), not a concept the API or
 * `schemas/broker.ts` knows about, so there is nothing to import here.
 */
export const BROKER_TABS = ['overview', 'activity', 'specialty'] as const
export const brokerTabSchema = z.enum(BROKER_TABS)
export type BrokerTab = z.infer<typeof brokerTabSchema>

/**
 * By broker's URL is `brokerStocksQuerySchema` plus the tab.
 *
 * It used to be `{ tab, period }` alone, which left the page's big stocks
 * table — its own search field, sort menu and pager — in a `useState` inside
 * `BrokerScreen`. Every other table in the app puts those in the URL, and the
 * router deliberately does not reset scroll when only search params change, so
 * paging that table jumped a member to the top of a page they were reading
 * halfway down. A shared link also landed on page 1 unsorted whatever the
 * sender was looking at.
 *
 * Composed from `schemas/broker.ts` rather than restated: `page`/`pageSize`
 * are the app's paging contract, `sort` is `BROKER_STOCK_SORTS` (the closed,
 * direction-fixed set `/brokers/{code}/stocks/` accepts, NOT the generic
 * `sortBy`/`sortDesc` pair, which could not honestly represent it), and
 * `period` is the family toggle — one `z.enum` call per concept.
 *
 * `useBrokerDetail(code, period)` takes `code` (a route param, read separately
 * via `Route.useParams()`) and `period` positionally, so
 * `Route.useSearch().period` still passes straight through.
 */
export const brokerSearchSchema = brokerStocksQuerySchema.extend({
  tab: brokerTabSchema.default('overview'),
})
export type BrokerSearch = z.infer<typeof brokerSearchSchema>

/**
 * Projects the By-broker URL down to what the stocks endpoint takes.
 *
 * `tab` MUST NOT reach `useBrokerStocks`: `brokerKeys.stocks` puts the whole
 * query object in the key (keys.ts says why), so a `tab` riding along would
 * make switching Overview→Activity — a client-side toggle over a payload
 * already in hand — refetch the stocks table under it. The same one-named-
 * converter pattern as `toLargeTradeLeadersQuery` below.
 */
export const toBrokerStocksQuery = (search: BrokerSearch): BrokerStocksQuery => ({
  page: search.page,
  pageSize: search.pageSize,
  period: search.period,
  sort: search.sort,
  ...(search.q === undefined ? {} : { q: search.q }),
})

// --- broker reviews --------------------------------------------------------------------

/**
 * Reviews is a sibling route of `/brokers/$code` (Figma `1328:2`), not a
 * fourth tab inside `brokerSearchSchema` above — see `BrokerReviewsScreen.tsx`.
 * `code` is a route param, read separately via `Route.useParams()`; a review
 * is not a windowed measure, so there is no `period` here, only paging.
 *
 * This route's `validateSearch` IS `brokerReviewsQuerySchema` — the exact
 * query type `useBrokerReviews` expects — not a lookalike built from paging
 * fields restated by hand. It inherits `schemas/broker.ts`'s shared
 * `pagedQuery` shape (`page` defaults to 1, `pageSize` to 20, same as Large
 * trades and By-broker's `stocks`/`floorsheet` tabs). All brokers is the one
 * paged broker surface that overrides that default down to 10 — see
 * `allBrokersSearchSchema` below for why — so the 10-vs-20 split across
 * broker endpoints is a known, deliberate inconsistency this schema does not
 * paper over.
 *
 * `Route.useSearch()` is NOT handed straight to `useBrokerReviews(code, query)`
 * any more — go through `toBrokerReviewsQuery` below.
 */
export const brokerReviewsSearchSchema = brokerReviewsQuerySchema
export type BrokerReviewsSearch = BrokerReviewsQuery

/**
 * Projects the Reviews URL down to what the endpoint takes.
 *
 * `brokerReviewsSearchSchema` IS `brokerReviewsQuerySchema` by reference, so
 * this reads like the identity function — it used to be one. TanStack
 * Router's search validation MERGES a route's parsed search back onto
 * whatever the raw URL held (confirmed against `@tanstack/router-core`), so
 * `Route.useSearch()` can carry keys this schema never declared (an
 * `?utm_source=`, a stray param from a pasted link) that a direct
 * `.parse()` of the URL — what the loader does — already strips. Handing the
 * two different objects to the same `brokerKeys.reviews` cache key split one
 * request into two: the loader warmed the clean key, the screen read the
 * dirty one and fetched again. One picker, used by both, is what keeps them
 * agreeing — the same fix `toLargeHoldingsQuery` already has.
 */
export const toBrokerReviewsQuery = (search: BrokerReviewsSearch): BrokerReviewsQuery => ({
  page: search.page,
  pageSize: search.pageSize,
})

// --- all brokers ---------------------------------------------------------------------

/**
 * All brokers' `validateSearch` IS `brokerListQuerySchema` — the exact query
 * type `useBrokerList` expects — not a lookalike built from the app's generic
 * `tableQuerySchema`. It used to be the latter (page/pageSize/sortBy/sortDesc,
 * with `q` and `period` left out as "not asked for in this wave"); that was
 * the duplication this file's reconciliation removed. `GET /api/v5/brokers/`
 * has a closed, direction-fixed sort (`BROKER_LIST_SORTS`: `name` ascends,
 * `net`/`turnover` descend — there is no `order` param), so the generic
 * sortBy/sortDesc pair could not honestly represent it anyway.
 *
 * `Route.useSearch()` is NOT handed straight to `useBrokerList(query)` any
 * more — go through `toBrokerListQuery` below.
 */
export const allBrokersSearchSchema = brokerListQuerySchema
export type AllBrokersSearch = BrokerListQuery

/**
 * Projects the All brokers URL down to what the list endpoint takes.
 *
 * `allBrokersSearchSchema` IS `brokerListQuerySchema` by reference, so this
 * looks like the identity function — it is not, and used not to be one. See
 * `toBrokerReviewsQuery`'s own comment for the mechanism: TanStack Router's
 * search validation merges a route's parsed search back onto the raw URL, so
 * `Route.useSearch()` can carry a key this schema never declared even though
 * a direct `.parse()` — what the loader runs — already strips it. Passing
 * `Route.useSearch()` straight to `useBrokerList` put that stray key in the
 * cache key `brokerKeys.list` hashes, splitting one request into two.
 */
export const toBrokerListQuery = (search: AllBrokersSearch): BrokerListQuery => ({
  page: search.page,
  pageSize: search.pageSize,
  period: search.period,
  sort: search.sort,
  ...(search.q === undefined ? {} : { q: search.q }),
})

// --- by stock --------------------------------------------------------------------------

/**
 * `symbol` is a route param (Figma `2325:16728`), read separately via
 * `Route.useParams()` — not part of this search schema. The URL-level state By
 * stock has is the family `period` toggle, or, from the calendar button, a
 * custom `start`/`end` (the one broker endpoint that takes a range). Both
 * dates are optional and fall back to absent when hand-edited into something
 * that is not a real day; `toByStockRange` below decides whether the pair is a
 * usable range.
 *
 * `useBrokersByStock(symbol, period, range)` takes `period` positionally, so
 * `Route.useSearch().period` passes straight through; the range goes through
 * `toByStockRange`.
 */
export const byStockSearchSchema = z.object({
  period: periodSchema.default(DEFAULT_PERIOD),
  start: calendarDateSchema.optional().catch(undefined),
  end: calendarDateSchema.optional().catch(undefined),
})
export type ByStockSearch = z.infer<typeof byStockSearchSchema>

/** mm-be's cap on a custom range (`ByStockRequestSerializer.MAX_SPAN_DAYS`); past it the read is a 400. */
export const BY_STOCK_MAX_RANGE_DAYS = 366

/**
 * The custom range the URL asks for, or null for "use the period".
 *
 * Both ends or neither, in order, and within the year mm-be allows: a half
 * range, an inverted one or one past the cap would reach the endpoint as a 400
 * that "Try again" would repeat forever, so a stale or hand-edited link falls
 * back to the period chips instead. A custom range wins over `period`
 * server-side, so the chips show no selection while this is non-null.
 */
export const toByStockRange = (search: ByStockSearch): BrokerDateRange | null => {
  const { start, end } = search
  if (start === undefined || end === undefined || start > end) return null
  // `YYYY-MM-DD` parses as UTC midnight, so the difference is whole days. Inline, not
  // `countInclusiveDays`: a route loader is not code-split and imports this module.
  const days = (Date.parse(end) - Date.parse(start)) / 86_400_000 + 1
  return days > BY_STOCK_MAX_RANGE_DAYS ? null : { start, end }
}

// --- compare -------------------------------------------------------------------------

/**
 * 2 to 4 distinct broker codes.
 *
 * Not encoded as the backend's own comma-joined `brokers=58,33` query param.
 * TanStack Router's default search codec JSON-round-trips any non-primitive
 * value handed to `navigate()` (see `@tanstack/router-core`'s
 * `searchParams.ts`): the instant a screen calls
 * `navigate({ search: { codes: [...] } })`, the URL becomes
 * `codes=%5B%2258%22...%5D`, and `JSON.parse` hands the next read back a real
 * array, not a comma string. A schema typed as a plain `z.string()` would
 * parse the first, hand-typed load and then reject every load after the
 * app's own navigation touched the URL.
 *
 * This schema accepts either shape on the way in — a real array (what the
 * router round-trips) or a comma string (what a hand-typed or pasted link
 * looks like, matching the API's own format) — and always produces a real
 * array, which is what a screen should hold as state and what `navigate()`
 * should be given back.
 *
 * The API's own comma-string encoding (`brokers=58,33`) is a SERVICE-layer
 * concern, not a URL one — `formatBrokerCodes`/`parseBrokerCodes` in
 * `schemas/broker.ts` do that conversion where the wire is actually read,
 * which is why this schema does not import or reuse them: it never talks to
 * the wire, only to the URL.
 */
export const compareCodesSchema = z
  .union([
    z.array(z.string().min(1)),
    z
      .string()
      .min(1)
      .transform((raw) =>
        raw
          .split(',')
          .map((code) => code.trim())
          .filter((code) => code.length > 0),
      ),
  ])
  .pipe(
    z
      .array(z.string().min(1))
      .min(2, 'Select at least 2 brokers to compare')
      .max(4, 'Compare supports up to 4 brokers')
      .refine((codes) => new Set(codes).size === codes.length, {
        message: 'Broker codes must be distinct',
      }),
  )
export type CompareCodes = z.infer<typeof compareCodesSchema>

/**
 * The three sub-tabs below "THE NUMBERS" (Figma `3167:2` with `3170:2` Stocks
 * in common and `3170:688` Top stocks).
 *
 * Route-only vocabulary, like `BROKER_TABS` above and for the same reason:
 * they are client-side views over data already in hand — `sizes` reads
 * `Compare.brokers[].tradeSizes`, `common` the intersection endpoint, `top` a
 * bounded fan-out — not a parameter any endpoint takes, so `schemas/broker.ts`
 * has nothing to import here. Declared ONCE, in this file: `CompareView` used
 * to hold its own `SUB_TABS` tuple and its own `isSubTab` guard, which is the
 * duplication that appears the moment a tab moves into the URL.
 */
export const COMPARE_TABS = ['sizes', 'common', 'top'] as const
export const compareTabSchema = z.enum(COMPARE_TABS)
export type CompareTab = z.infer<typeof compareTabSchema>

/**
 * `codes` is optional at the route level — no selection yet is the Empty
 * state (Figma `3170:1372`), not a validation error.
 *
 * `tab` is URL state for the ordinary reason: a member who has picked four
 * brokers and opened Top stocks is looking at something worth sending someone,
 * and the link should open it. It defaults to Stocks in common, the mock's
 * opening sub-tab.
 *
 * No paging: Stocks in common ends on the mock's "Show all N stocks" rather
 * than a pager, so how much of the list is open is the table's own state, not
 * the link's.
 *
 * No `period` field: Compare's Ready-for-Dev frames (`3167:2`, `3170:2`,
 * `3170:688`, `3170:1372`) carry no period toggle, unlike Overview/By broker/
 * All brokers. `schemas/broker.ts`'s `compareQuerySchema` does have a
 * `period` (the API accepts one), but nothing in this wave's design exposes
 * it, so there is no route default to encode. `useBrokerCompare` and
 * `useStocksInCommon` both take `(brokers, period)` positionally rather than
 * a query object — a screen reads `codes ?? []` for `brokers` and passes a
 * literal `'today'` for `period` until a period control is designed; if one
 * is, add `period: brokerPeriodSchema.default('today')` here rather than
 * inventing a second period enum.
 */
export const compareSearchSchema = z.object({
  codes: compareCodesSchema.optional(),
  tab: compareTabSchema.default('common'),
})
export type CompareSearch = z.infer<typeof compareSearchSchema>

// --- most traded ---------------------------------------------------------------------

/**
 * Most traded's `validateSearch` IS `busyNamesQuerySchema` — the exact query
 * type `useBusyNames` expects (`page`/`pageSize`/`period`/`sector`), not just
 * the sector chip. It used to carry only `{ sector? }`; paging and period
 * were left out as "not asked for in this wave", which meant `useBusyNames`
 * could not be called with `Route.useSearch()` directly. `sector` stays
 * optional — absent means "All sectors" (Figma `2650:17391`).
 *
 * `Route.useSearch()` is NOT handed straight to `useBusyNames(query)` any
 * more — go through `toBusyNamesQuery` below.
 */
export const mostTradedSearchSchema = busyNamesQuerySchema
export type MostTradedSearch = BusyNamesQuery

/**
 * Projects the Most traded URL down to what the busy-names endpoint takes.
 *
 * `mostTradedSearchSchema` IS `busyNamesQuerySchema` by reference, so this
 * looks like the identity function — see `toBrokerListQuery`'s own comment
 * for why it is not: TanStack Router's search merge can leave a stray key on
 * `Route.useSearch()` that a direct `.parse()` (the loader's own read)
 * already dropped, and passing the two different objects to `useBusyNames`
 * and the loader's `busyNamesQueryOptions` split one request into two —
 * confirmed with a bare `?utm_source=mail`.
 */
export const toBusyNamesQuery = (search: MostTradedSearch): BusyNamesQuery => ({
  page: search.page,
  pageSize: search.pageSize,
  period: search.period,
  ...(search.sector === undefined ? {} : { sector: search.sector }),
})

// --- sector flow ---------------------------------------------------------------------

/**
 * `period` is the family toggle. `sector` is optional and names a DIFFERENT
 * hook, not a filter over one hook's result: absent, the screen shows the
 * sector-flow overview across every sector (`useSectorFlow(period)`,
 * `GET /api/v5/brokers/sector-flow/`, Figma primary frame `3186:2`); present,
 * it shows that sector's ranked broker list (`useSectorBrokers(sector,
 * period)`, `GET /api/v5/brokers/sector-flow/{sector}/`). There is no default
 * sector — selecting one is a user action, not a page-load state — and this
 * schema does not invent one.
 *
 * Sector flow is precomputed-only with no live fallback (see
 * `schemas/broker.ts`'s `sectorFlowSchema`), which is a payload fact, not a
 * URL one, so it has no bearing on this schema.
 *
 * Both hooks take their arguments positionally, so `Route.useSearch().period`
 * (and, once a sector is picked, `.sector`) pass straight through with no
 * conversion.
 */
export const sectorFlowSearchSchema = z.object({
  /**
   * PINNED TO THE LITERAL `'today'`, not the four-value `periodSchema`.
   *
   * The endpoint accepts a period and the frame draws no control for one, so
   * this used to carry the full enum and forward whatever the URL held. Three
   * labels on the screen then hard-coded the word "today" — the card title,
   * the `Net today` column header and the table caption — which meant
   * `/brokers/sector-flow?period=3m` fetched and rendered three months of
   * sector flow under the word "today", three times, with nothing on screen to
   * contradict it. Nothing in the app generates such a URL, but a hand-edited
   * or shared one gets there.
   *
   * Pinning is the honest half of the pair: the labels are now true by
   * construction. The day a period control is designed for this screen, widen
   * this back to `periodSchema` AND change those three labels in the same
   * commit — they are the other half.
   */
  period: z.literal(DEFAULT_PERIOD).default(DEFAULT_PERIOD),
  sector: z.string().min(1).optional(),
})
export type SectorFlowSearch = z.infer<typeof sectorFlowSearchSchema>

// --- large trades --------------------------------------------------------------------

/**
 * The sortable heads of the Large trades table, and the direction. Neither is a request parameter
 * yet: the endpoint ranks biggest-first and takes no `order`
 * (`LARGE_TRADES_SORT_AVAILABLE`), so any pick but Amount high-to-low reads "Not available yet".
 */
export const LARGE_TRADE_SORTS = ['time', 'symbol', 'shares', 'amount'] as const
export type LargeTradeSort = (typeof LARGE_TRADE_SORTS)[number]

/**
 * Large trades' URL is `largeTradesQuerySchema` MINUS `size`, PLUS the table's sort.
 *
 * The mock fixes the floor at 10 L and draws no Size control, so a `?size=` the screen cannot show
 * would be the sector-flow hazard again (a table filtered under a heading that says otherwise).
 * `toLargeTradesQuery` pins `'10l'`. `sort` / `dir` ride the URL because a sorted table is a link,
 * though nothing is sent yet.
 */
export const largeTradesSearchSchema = largeTradesQuerySchema.omit({ size: true }).extend({
  sort: z.enum(LARGE_TRADE_SORTS).optional(),
  dir: z.enum(['asc', 'desc']).optional(),
})
export type LargeTradesSearch = z.infer<typeof largeTradesSearchSchema>

/** The floor every Large trades read uses, as the mock's "Rs 10 L and up" pins it. */
const LARGE_TRADES_FLOOR: LargeTradeSize = '10l'

/**
 * Projects the Large trades URL down to what the trades endpoint itself takes — as opposed to
 * `toLargeTradeLeadersQuery` below, which projects it down to the narrower leaderboard one.
 *
 * Not the identity: TanStack Router's search merge can leave a stray key on `Route.useSearch()`
 * that a direct `.parse()` (the loader's own read) already dropped, and passing it straight to
 * `useLargeTrades` put that stray key in the cache key `brokerKeys.largeTrades` hashes, splitting
 * one request into two. `sort` / `dir` are left off for the same reason — the endpoint takes none.
 */
export const toLargeTradesQuery = (search: LargeTradesSearch): LargeTradesQuery => ({
  page: search.page,
  pageSize: search.pageSize,
  size: LARGE_TRADES_FLOOR,
  ...(search.stock === undefined ? {} : { stock: search.stock }),
  ...(search.broker === undefined ? {} : { broker: search.broker }),
})

/**
 * The summary band and the Stock / Broker menus' options: the day's big trades at the floor,
 * whatever the table is filtered to (owner ruling 4: the band always says "today"). One read of
 * the 100 biggest, the widest page the app accepts; the table's own read is a different key unless
 * it is the same page.
 */
export const toLargeTradesTodayQuery = (): LargeTradesQuery => ({
  page: 1,
  pageSize: 100,
  size: LARGE_TRADES_FLOOR,
})

/**
 * The leaderboard: the day's busiest houses at the floor, ignoring the Stock filter like the band
 * (the mock's leaders are a standing list; a row picks its broker into the table).
 */
export const toLargeTradeLeadersQuery = (): LargeTradeLeadersQuery => ({
  size: LARGE_TRADES_FLOOR,
})

// --- large holdings ------------------------------------------------------------------

/**
 * Large holdings' URL is `largeHoldingsQuerySchema` MINUS `sector`.
 *
 * The one place in this file where a route schema deliberately narrows the
 * endpoint query rather than being it. V2's frame (`4243:103846`) draws exactly
 * one control — the period segment — and no sector chip, and a `?sector=` that
 * nothing on screen names would be the sector-flow hazard in a new place: the
 * table would silently show one sector's rows under a heading that claims to
 * rank the whole market, with nothing to contradict it. Most traded can carry
 * `sector` in its URL because it draws a `SectorFilter` that says so.
 *
 * `period` keeps `largeHoldingsQuerySchema`'s `1m` default — the backend's own,
 * and the chip V2 draws selected — so a bare `/brokers/large-holdings` and a
 * bare `GET /api/v5/brokers/large-holdings/` agree on the window.
 *
 * The day a sector control is designed for this screen, this becomes
 * `largeHoldingsQuerySchema` outright and `toLargeHoldingsQuery` below becomes
 * an identity that can be deleted — one line each, in the same commit as the
 * control.
 */
export const LARGE_HOLDINGS_SORTS = ['symbol', 'code', 'netQty', 'avgRate', 'pctOfCapital'] as const
export type LargeHoldingsSort = (typeof LARGE_HOLDINGS_SORTS)[number]

export const largeHoldingsSearchSchema = largeHoldingsQuerySchema.omit({ sector: true }).extend({
  /** The table's sorted head. Not a request parameter yet (`LARGE_HOLDINGS_SORT_AVAILABLE`). */
  sort: z.enum(LARGE_HOLDINGS_SORTS).optional(),
  dir: z.enum(['asc', 'desc']).optional(),
})
export type LargeHoldingsSearch = z.infer<typeof largeHoldingsSearchSchema>

/**
 * Projects the Large holdings URL up to what the endpoint takes.
 *
 * `sector` is left off rather than passed as `undefined`: `brokerKeys.largeHoldings`
 * hashes the whole query object, and React Query's hash treats an absent key
 * and an explicit `undefined` as the same value — but the service's own
 * spread (`...(query.sector === undefined ? {} : ...)`) is what keeps the
 * parameter out of the URL either way. The same one-named-converter shape as
 * `toLargeTradeLeadersQuery` and `toBrokerStocksQuery`.
 */
export const toLargeHoldingsQuery = (search: LargeHoldingsSearch): LargeHoldingsQuery => ({
  page: search.page,
  pageSize: search.pageSize,
  period: search.period,
})

// --- pv range ------------------------------------------------------------------------

/**
 * PV range's `validateSearch` IS `pvRangeQuerySchema` — `period`, `buyer?`,
 * `seller?` — the exact query type `usePVRange` takes as its second argument.
 * `symbol` is a route param (`/brokers/pv-range/$symbol`), read separately via
 * `Route.useParams()`, exactly as By stock reads its own.
 *
 * No paging: the endpoint has none, and a page of a volume profile would be a
 * chart with its middle missing. Every one of the three fields has a control on
 * V2's frame (`4243:104586`: a Buyer select, a Seller select and the period
 * segment), so unlike Large holdings above there is nothing here that the URL
 * can hold and the screen cannot show.
 *
 * `Route.useSearch()` is NOT handed straight to `usePVRange` any more — go
 * through `toPVRangeQuery` below.
 */
export const pvRangeSearchSchema = pvRangeQuerySchema
export type PVRangeSearch = PVRangeQuery

/**
 * Projects the PV range URL down to what the endpoint takes.
 *
 * `pvRangeSearchSchema` IS `pvRangeQuerySchema` by reference, so this looks
 * like the identity function — see `toBrokerListQuery`'s own comment for why
 * it is not: TanStack Router's search merge can leave a stray key on
 * `Route.useSearch()` that a direct `.parse()` (the loader's own read)
 * already dropped, and passing `Route.useSearch()` straight to `usePVRange`
 * put that stray key in the cache key `brokerKeys.pvRange` hashes, splitting
 * one request into two.
 */
export const toPVRangeQuery = (search: PVRangeSearch): PVRangeQuery => ({
  period: search.period,
  ...(search.buyer === undefined ? {} : { buyer: search.buyer }),
  ...(search.seller === undefined ? {} : { seller: search.seller }),
})
